import { isTerminalState, type SessionDetailView } from '@era/shared'

const MEDIA_QUALITY_INGEST_GRACE_MS = 15 * 60_000
const MEDIA_QUALITY_CLOCK_SKEW_MS = 15_000

/**
 * Browser diagnostics have a bounded life after the media products settle.
 *
 * A connection's `final` bit is monotonic in the controller, but pagehide can
 * set it before a bfcache-restored call continues. Treat it as final for this
 * screen only once the report was received at the participant's leave/end
 * boundary. The controller rejects all reports after the 15-minute grace, so
 * that deadline is the hard stop when a browser never manages its final send.
 */
export function isMediaQualitySettled(
  view: SessionDetailView,
  nowMs = Date.now(),
): boolean {
  const quality = view.media_quality
  // Rolling/retained snapshots created before browser QoE remain finalizable.
  if (!quality) return true
  if (quality.expected_observers === 0) return true

  const endedMs = view.ended_at ? Date.parse(view.ended_at) : Number.NaN
  if (Number.isFinite(endedMs) && nowMs >= endedMs + MEDIA_QUALITY_INGEST_GRACE_MS) return true

  const boundaryByIdentity = new Map<string, number>()
  for (const participant of view.participants) {
    if (!participant.joined_at) continue
    const boundary = participant.left_at ?? view.ended_at
    const boundaryMs = boundary ? Date.parse(boundary) : Number.NaN
    if (Number.isFinite(boundaryMs)) boundaryByIdentity.set(participant.identity, boundaryMs)
  }

  const finalObservers = new Set<string>()
  for (const connection of quality.connections) {
    if (!connection.final) continue
    const receivedMs = Date.parse(connection.last_received_at)
    const boundaryMs = boundaryByIdentity.get(connection.observer_identity) ?? endedMs
    if (
      Number.isFinite(receivedMs) &&
      Number.isFinite(boundaryMs) &&
      receivedMs >= boundaryMs - MEDIA_QUALITY_CLOCK_SKEW_MS
    ) {
      finalObservers.add(connection.observer_identity)
    }
  }
  return finalObservers.size >= quality.expected_observers
}

/** QoE ingest emits no browser-facing SSE, so an open detail view polls it. */
export function needsMediaQualityPolling(
  view: SessionDetailView | undefined,
  nowMs = Date.now(),
): boolean {
  if (!view?.media_quality) return false
  // Before the first participant/report there is no boundary to test, but the
  // controller's collecting state can become non-empty at the next upload.
  return view.media_quality.status === 'collecting' || !isMediaQualitySettled(view, nowMs)
}

/** True only once products and the bounded browser-QoE window are settled. */
export function isSessionSettled(
  view: SessionDetailView | undefined,
  nowMs = Date.now(),
): boolean {
  if (!view || !isTerminalState(view.state)) return false

  let productsSettled = view.state === 'failed' || view.state === 'purged'
  if (!productsSettled) {
    productsSettled = view.recording_ever_enabled === false ||
      view.transcript.artifact_id !== null ||
      view.pipeline.state === 'ready' ||
      view.pipeline.state === 'failed' ||
      view.pipeline.state === 'cancelled'
  }

  return productsSettled && isMediaQualitySettled(view, nowMs)
}
